import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Download,
  ArrowLeft,
  Loader2,
} from "lucide-react";
import Logo from "@/components/landing/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SigneringPage = () => {
  const { status } = useParams();
  const [params] = useSearchParams();
  const ordreId = params.get("ordre_id") || "";
  const [signStatus, setSignStatus] = useState(status === "ferdig" ? "VENTER" : status);

  useEffect(() => {
    document.title = "Signering – Rstrøm";
  }, []);

  useEffect(() => {
    if (status !== "ferdig" || !ordreId) return;
    let stop = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/bestill/${ordreId}/signeringsstatus`);
        if (!stop && r.data.status === "SIGNERT") setSignStatus("SIGNERT");
      } catch {
        /* nettverksfeil – prøver igjen ved neste intervall */
      }
    };
    poll();
    const interval = setInterval(poll, 3000);
    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!stop) setSignStatus((s) => (s === "SIGNERT" ? s : "TIDSAVBRUDD"));
    }, 90000);
    return () => {
      stop = true;
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [status, ordreId]);

  const content = {
    SIGNERT: {
      icon: <CheckCircle2 className="w-10 h-10" />,
      iconClass: "bg-emerald-100 border-emerald-300 text-emerald-600",
      title: "Avtalen er signert!",
      text: "Takk – kundeavtalen din er signert med BankID. Du finner den signerte avtalen under Mine sider, og kan også laste den ned her.",
      testid: "signing-success",
    },
    VENTER: {
      icon: <Loader2 className="w-10 h-10 animate-spin" />,
      iconClass: "bg-sky-100 border-sky-300 text-sky-600",
      title: "Behandler signeringen …",
      text: "Vi mottok signeringen din og henter det ferdige dokumentet. Dette tar vanligvis bare noen sekunder.",
      testid: "signing-pending",
    },
    TIDSAVBRUDD: {
      icon: <AlertTriangle className="w-10 h-10" />,
      iconClass: "bg-amber-100 border-amber-300 text-amber-600",
      title: "Det tar litt lengre tid enn vanlig",
      text: "Signeringen din er registrert, men dokumentet er ikke klart ennå. Du finner den signerte avtalen under Mine sider så snart den er klar.",
      testid: "signing-timeout",
    },
    avbrutt: {
      icon: <XCircle className="w-10 h-10" />,
      iconClass: "bg-slate-100 border-slate-300 text-slate-500",
      title: "Signeringen ble avbrutt",
      text: "Ingen avtale er signert. Du kan prøve igjen når det passer – bestillingen din er fortsatt registrert.",
      testid: "signing-cancelled",
    },
    feil: {
      icon: <XCircle className="w-10 h-10" />,
      iconClass: "bg-red-100 border-red-300 text-red-600",
      title: "Noe gikk galt under signeringen",
      text: "Signeringen kunne ikke fullføres. Prøv igjen, eller kontakt oss på post@rstrom.no hvis problemet vedvarer.",
      testid: "signing-error",
    },
  }[signStatus] || {
    icon: <AlertTriangle className="w-10 h-10" />,
    iconClass: "bg-slate-100 border-slate-300 text-slate-500",
    title: "Ukjent status",
    text: "Vi fant ikke statusen for signeringen.",
    testid: "signing-unknown",
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A]" data-testid="signering-page">
      <header className="bg-[#0B132B]/95 border-b border-white/10 text-white">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <a href="/" aria-label="Til forsiden" data-testid="signering-logo-link">
            <Logo />
          </a>
        </div>
      </header>
      <section className="py-24">
        <div className="max-w-xl mx-auto px-6 text-center" data-testid={content.testid}>
          <div className={`w-20 h-20 mx-auto rounded-full border flex items-center justify-center mb-8 ${content.iconClass}`}>
            {content.icon}
          </div>
          <h1 className="font-serif-heading text-3xl font-bold text-[#0B132B] mb-4">{content.title}</h1>
          <p className="text-slate-600 leading-relaxed mb-8">{content.text}</p>
          {ordreId && (
            <p className="text-sm text-slate-500 mb-8">
              Ordrenummer: <strong data-testid="signing-order-id">{ordreId}</strong>
            </p>
          )}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {signStatus === "SIGNERT" && ordreId && (
              <a
                href={`${API}/ordre/${ordreId}/signert.pdf`}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#0B132B] font-bold text-sm transition-colors"
                data-testid="download-signed-button"
              >
                <Download className="w-4 h-4" /> Last ned signert avtale
              </a>
            )}
            <a
              href="/mine-sider"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#0B132B] hover:bg-[#1a2547] text-white font-bold text-sm transition-colors"
              data-testid="signing-portal-link"
            >
              Til Mine sider
            </a>
            <a
              href="/"
              className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-600 hover:text-emerald-500"
              data-testid="signing-home-link"
            >
              <ArrowLeft className="w-4 h-4" /> Til forsiden
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SigneringPage;
